class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.stepConfig = {
      1: { required: parseInt(this.dataset.stepOneQty, 10) || 2 },
      2: { required: parseInt(this.dataset.stepTwoQty, 10) || 1 },
    };
    this.bundleName = this.dataset.bundleName || 'Bundle';
    this.addLabel = this.dataset.btnAdd || 'ADD TO BOX';
    this.removeLabel = this.dataset.btnRemove || 'REMOVE ITEM';
    this.fixedPrice = this.dataset.bundlePrice || '';
    this.parentVariantId = this.dataset.bundleParentVariant || '';
    this.bundleImage = this.dataset.bundleImage || '';
    this.freebieVariant = this.dataset.freebieVariant || '';
    this.currentStep = 1;
    this.selections = { 1: new Map(), 2: new Map() };
    this.isSubmitting = false;

    this.stickyBar = this.querySelector('[data-bundle-sticky]');
    this.countEl = this.querySelector('[data-bundle-count]');
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.atcBtn = this.querySelector('[data-bundle-atc]');

    this.addEventListener('click', this.handleClick.bind(this));

    if (this.stickyBar) {
      this.stickyBar.hidden = false;
    }

    this.cartStatusEl = this.querySelector('[data-bundle-cart-info]');

    // On page load, fetch cart from server and show bundle item count
    this.fetchCartCount();

    // Handle browser back button (bfcache restore)
    this.handlePageShow = this.handlePageShow.bind(this);
    window.addEventListener('pageshow', this.handlePageShow);

    this.updateUI();
  }

  /**
   * Increments a visit counter stored in Shopify cart attributes.
   * 1. GET  /cart.js  → read current count from cart.attributes._visit_count
   * 2. POST /cart/update.js → write count + 1 back to cart attributes
   * 3. Display the new count in the container
   *
   * Cart attributes live on Shopify's server (session-based),
   * so the count survives every refresh — no browser storage needed.
   */
  async fetchCartCount() {
    if (!this.cartStatusEl) return;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await (await fetch(`${root}cart.js`)).json();
      const currentCount = parseInt(cart.attributes?._visit_count, 10) || 0;
      const newCount = currentCount + 1;

      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _visit_count: String(newCount) } }),
      });

      this.cartStatusEl.textContent = `Page loaded ${newCount} time(s)`;

      // Clear cart items after counter is saved
      // (cart/clear.js removes items only, attributes like _visit_count are preserved)
      await fetch(`${root}cart/clear.js`, { method: 'POST' });
      if (typeof reload_cartx === 'function') {
        reload_cartx();
      }
      document.querySelectorAll('.header__cart-badge, .mobile-menu-header__cart-badge').forEach((badge) => {
        badge.textContent = '0';
        badge.dataset.cartCount = '0';
        badge.hidden = true;
      });
    } catch {
      this.cartStatusEl.textContent = 'Could not load count';
    }
  }

  /**
   * Removes any cart items that have a `_bundle_id` property.
   * This ensures that when a user cancels payment and comes back,
   * the cart doesn't have leftover bundle products.
   */
  async clearBundleItemsFromCart() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`);
      if (!response.ok) return;

      const cart = await response.json();
      if (!cart.items?.length) return;

      // Find bundle items by checking for _bundle_id property
      const bundleItems = cart.items.filter(
        (item) => item.properties?._bundle_id
      );

      if (!bundleItems.length) return;

      // Build updates object — set quantity to 0 for each bundle item
      const updates = {};
      for (const item of bundleItems) {
        updates[item.key] = 0;
      }

      const updateResponse = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      if (updateResponse.ok) {
        const updatedCart = await updateResponse.json();
        // Update cart count in header and any cart UI
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = updatedCart.item_count;
        });
        // Dispatch event so other components (cart drawer, header) can update
        document.dispatchEvent(new CustomEvent('theme:cart:updated', {
          detail: { cart: updatedCart },
          bubbles: true,
        }));
      }
    } catch {
      // Silently fail — don't block page load
    }
  }

  /**
   * Handles bfcache restore (browser back button).
   * If page was restored from cache, clear cart and reset.
   */
  async handlePageShow(event) {
    if (!event.persisted) return;
    await this.clearBundleItemsFromCart();
    this.resetToStart();
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  handleClick(event) {
    const toggleBtn = event.target.closest('[data-bundle-toggle]');
    const stepBtn = event.target.closest('[data-bundle-step-btn]');
    const atcBtn = event.target.closest('[data-bundle-atc]');

    if (toggleBtn) {
      event.preventDefault();
      event.stopPropagation();
      this.handleToggle(toggleBtn);
      return;
    }

    if (stepBtn) {
      const step = parseInt(stepBtn.dataset.bundleStepBtn, 10);
      this.goToStep(step);
      return;
    }

    if (atcBtn) {
      this.handleAddToCart();
      return;
    }

    const bundleWrap = event.target.closest('[data-bundle-product]');
    if (bundleWrap) {
      const link = event.target.closest('a');
      if (link) {
        event.preventDefault();
      }
    }
  }

  handleToggle(button) {
    const card = button.closest('[data-bundle-product]');
    if (!card) return;

    const step = parseInt(card.dataset.step, 10);
    const productId = card.dataset.productId;
    const variantId = card.dataset.variantId;
    const price = parseInt(card.dataset.productPrice, 10);
    const stepSelections = this.selections[step];
    const required = this.stepConfig[step].required;

    if (stepSelections.has(productId)) {
      stepSelections.delete(productId);
    } else {
      if (stepSelections.size >= required) return;
      stepSelections.set(productId, { variantId, price });
    }

    this.updateUI();

    if (stepSelections.size === required && step === 1 && this.currentStep === 1) {
      setTimeout(() => this.goToStep(2), 400);
    }
  }

  goToStep(step) {
    if (step === this.currentStep) return;
    this.currentStep = step;

    const stepPanels = this.querySelectorAll('[data-bundle-step]');
    for (const panel of stepPanels) {
      const panelStep = parseInt(panel.dataset.bundleStep, 10);
      panel.hidden = panelStep !== step;
    }

    const stepBtns = this.querySelectorAll('[data-bundle-step-btn]');
    for (const btn of stepBtns) {
      const btnStep = parseInt(btn.dataset.bundleStepBtn, 10);
      if (btnStep === step) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-current', 'step');
      } else {
        btn.classList.remove('is-active');
        btn.removeAttribute('aria-current');
      }
    }

    const stepsEl = this.querySelector('[data-bundle-steps]');
    if (stepsEl) {
      stepsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * After checkout is triggered, watch for Gokwik popup closing.
   * Uses polling + MutationObserver + visibility/focus as fallbacks.
   */
  waitForCheckoutReturn() {
    this.checkoutTriggered = true;

    // Poll for Gokwik popup removal every 500ms
    const pollInterval = setInterval(async () => {
      if (!this.checkoutTriggered) {
        clearInterval(pollInterval);
        return;
      }

      const gokwikPopup = document.querySelector(
        '#gokwik-iframe, #gokwik-checkout, .gokwik-container, [id*="gokwik"], iframe[src*="gokwik"]'
      );

      // If popup existed before but now gone → user cancelled
      if (this.gokwikWasOpen && !gokwikPopup) {
        this.checkoutTriggered = false;
        clearInterval(pollInterval);
        await this.clearBundleItemsFromCart();
        return;
      }

      if (gokwikPopup) {
        this.gokwikWasOpen = true;
      }
    }, 500);

    // Also listen for visibility/focus as fallback
    const onVisibilityChange = async () => {
      if (!this.checkoutTriggered) return;
      if (document.visibilityState !== 'visible') return;

      this.checkoutTriggered = false;
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);

      await new Promise((resolve) => setTimeout(resolve, 500));
      if (!document.querySelector('bundle-builder')) return;
      await this.clearBundleItemsFromCart();
    };

    const onFocus = async () => {
      if (!this.checkoutTriggered) return;

      this.checkoutTriggered = false;
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);

      await new Promise((resolve) => setTimeout(resolve, 500));
      if (!document.querySelector('bundle-builder')) return;
      await this.clearBundleItemsFromCart();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    // Safety timeout — clean up after 5 minutes
    setTimeout(() => {
      this.checkoutTriggered = false;
      clearInterval(pollInterval);
    }, 300000);
  }

  /**
   * Resets all selections and UI back to Step 1 starting state.
   */
  resetToStart() {
    this.selections = { 1: new Map(), 2: new Map() };
    this.currentStep = 1;

    const stepPanels = this.querySelectorAll('[data-bundle-step]');
    for (const panel of stepPanels) {
      const panelStep = parseInt(panel.dataset.bundleStep, 10);
      panel.hidden = panelStep !== 1;
    }

    const stepBtns = this.querySelectorAll('[data-bundle-step-btn]');
    for (const btn of stepBtns) {
      const btnStep = parseInt(btn.dataset.bundleStepBtn, 10);
      if (btnStep === 1) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-current', 'step');
      } else {
        btn.classList.remove('is-active');
        btn.removeAttribute('aria-current');
      }
    }

    this.updateUI();
  }

  updateUI() {
    this.updateCards();
    this.updateStepIndicators();
    this.updateStickyBar();
  }

  updateCards() {
    const cards = this.querySelectorAll('[data-bundle-product]');
    for (const card of cards) {
      const step = parseInt(card.dataset.step, 10);
      const productId = card.dataset.productId;
      const isAvailable = card.dataset.available === 'true';
      const btn = card.querySelector('[data-bundle-toggle]');
      if (!btn) continue;

      const isSelected = this.selections[step].has(productId);
      const isFull = this.selections[step].size >= this.stepConfig[step].required;

      if (isSelected) {
        card.classList.add('is-selected');
        btn.textContent = this.removeLabel;
        btn.classList.add('bundle-toggle-btn--selected');
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
      } else {
        card.classList.remove('is-selected');
        btn.textContent = this.addLabel;
        btn.classList.remove('bundle-toggle-btn--selected');

        if (isFull || !isAvailable) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
        }
      }
    }
  }

  updateStepIndicators() {
    for (const stepNum of [1, 2]) {
      const btn = this.querySelector(`[data-bundle-step-btn="${stepNum}"]`);
      const numberEl = btn?.querySelector('[data-step-number]');
      if (!btn || !numberEl) continue;

      const count = this.selections[stepNum].size;
      const required = this.stepConfig[stepNum].required;
      const isComplete = count >= required;

      if (isComplete) {
        btn.classList.add('is-complete');
        numberEl.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      } else {
        btn.classList.remove('is-complete');
        numberEl.textContent = stepNum;
      }
    }

    const connector = this.querySelector('[data-bundle-connector]');
    if (connector) {
      const step1Complete = this.selections[1].size >= this.stepConfig[1].required;
      if (step1Complete) {
        connector.classList.add('is-active');
      } else {
        connector.classList.remove('is-active');
      }
    }
  }

  updateStickyBar() {
    if (!this.stickyBar) return;

    const step1Count = this.selections[1].size;
    const step2Count = this.selections[2].size;
    const totalSelected = step1Count + step2Count;
    const totalRequired = this.stepConfig[1].required + this.stepConfig[2].required;

    if (this.countEl) {
      this.countEl.textContent = `${totalSelected}/${totalRequired} selected`;
    }

    if (this.totalEl && this.fixedPrice) {
      this.totalEl.textContent = `₹${this.fixedPrice}`;
    }

    const isComplete = step1Count >= this.stepConfig[1].required
      && step2Count >= this.stepConfig[2].required;

    if (this.atcBtn) {
      this.atcBtn.disabled = !isComplete || this.isSubmitting;
      this.atcBtn.setAttribute('aria-disabled', String(!isComplete || this.isSubmitting));
    }
  }

  async handleAddToCart() {
    if (this.isSubmitting) return;

    const step1Complete = this.selections[1].size >= this.stepConfig[1].required;
    const step2Complete = this.selections[2].size >= this.stepConfig[2].required;
    if (!step1Complete || !step2Complete) return;

    this.isSubmitting = true;
    this.updateStickyBar();

    const originalLabel = this.atcBtn?.textContent;
    if (this.atcBtn) {
      this.atcBtn.textContent = 'Adding...';
    }

    const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const items = [];

    for (const stepMap of Object.values(this.selections)) {
      for (const { variantId } of stepMap.values()) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            _bundle_id: bundleId,
            _bundle_name: this.bundleName,
            _bundle_price: this.fixedPrice,
            _bundle_parent_variant: this.parentVariantId,
            _bundle_image: this.bundleImage,
          },
        });
      }
    }

    // Add freebie product if configured
    if (this.freebieVariant) {
      items.push({
        id: parseInt(this.freebieVariant, 10),
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _is_freebie: 'true',
        },
      });
    }

    const body = { items };

    try {
      const root = window.Shopify?.routes?.root || '/';

      // Clear entire cart first — only bundle items should go to checkout
      await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw errorData;
      }

      await response.json();

      /* --- ATC: open cart drawer (commented out — keeping for future use) ---
      if (typeof fetchCart === 'function') {
        fetchCart();
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const overlay = document.querySelector('.cartx-drawer-overlay');
      if (cartDrawer && overlay) {
        cartDrawer.classList.add('drawer-open');
        overlay.classList.add('open-overlay');
        document.body.classList.add('cartx-open');
      }
      --- end ATC --- */

      // Buy Now: trigger GoKwik checkout
      // Listen for user returning after payment cancel
      this.waitForCheckoutReturn();

      if (typeof onCheckoutClick === 'function') {
        onCheckoutClick(this.atcBtn);
      } else {
        window.location.href = `${root}checkout`;
      }
    } catch (error) {
      const errorMsg = error?.description || error?.message || 'Could not add to cart. Please try again.';
      if (this.atcBtn) {
        this.atcBtn.textContent = errorMsg;
        setTimeout(() => {
          this.atcBtn.textContent = originalLabel;
        }, 3000);
      }
    } finally {
      this.isSubmitting = false;
      if (this.atcBtn) {
        this.atcBtn.textContent = originalLabel;
      }
      this.updateStickyBar();
    }
  }

}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
